import { useState } from 'react'
import { CheckCircle, XCircle, Clock, History } from 'lucide-react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import OTPModal from '@/components/features/approvals/OTPModal'
import RejectDialog from '@/components/features/approvals/RejectDialog'
import { approvalsApi } from '@/lib/api/approvals'
import type { PaymentListItem } from '@/lib/api/payments'

const formatAmount = (paise: number) =>
  new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', maximumFractionDigits: 0 }).format(paise / 100)

const formatDate = (iso: string) =>
  new Date(iso).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })

function statusBadge(status: string) {
  const map: Record<string, { label: string; className: string }> = {
    SUCCESS:    { label: 'Success',    className: 'text-emerald-700 border-emerald-300 bg-emerald-50' },
    APPROVED:   { label: 'Approved',   className: 'text-emerald-700 border-emerald-300 bg-emerald-50' },
    REJECTED:   { label: 'Rejected',   className: 'text-red-600 border-red-300 bg-red-50' },
    FAILED:     { label: 'Failed',     className: 'text-red-600 border-red-300 bg-red-50' },
    REVERSED:   { label: 'Reversed',   className: 'text-orange-600 border-orange-300 bg-orange-50' },
    CANCELLED:  { label: 'Expired',    className: 'text-slate-500 border-slate-300 bg-slate-50' },
    PROCESSING: { label: 'Processing', className: 'text-blue-600 border-blue-300 bg-blue-50' },
  }
  const cfg = map[status] ?? { label: status, className: 'text-slate-500 border-slate-300' }
  return <Badge variant="outline" className={`whitespace-nowrap text-xs ${cfg.className}`}>{cfg.label}</Badge>
}

// ─── Pending tab ──────────────────────────────────────────────────────────────

function PendingTab() {
  const queryClient = useQueryClient()
  const [otpPayment, setOtpPayment] = useState<PaymentListItem | null>(null)
  const [rejectPayment, setRejectPayment] = useState<PaymentListItem | null>(null)

  const { data, isLoading } = useQuery({
    queryKey: ['approvals'],
    queryFn: () => approvalsApi.list(),
    select: (res) => res.data,
    refetchInterval: 30_000,
  })

  function handleActionSuccess() {
    queryClient.invalidateQueries({ queryKey: ['approvals'] })
    queryClient.invalidateQueries({ queryKey: ['approvals-count'] })
    queryClient.invalidateQueries({ queryKey: ['approvals-history'] })
    queryClient.invalidateQueries({ queryKey: ['payments'] })
    queryClient.invalidateQueries({ queryKey: ['dashboard'] })
  }

  return (
    <>
      <div className="flex items-center gap-2 mb-4">
        <Clock className="h-4 w-4 text-amber-500" />
        <p className="text-sm text-muted-foreground">
          {data?.total ?? 0} payment{(data?.total ?? 0) !== 1 ? 's' : ''} awaiting review
        </p>
      </div>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="divide-y">
              {Array.from({ length: 4 }).map((_, i) => (
                <div key={i} className="flex items-center gap-4 px-6 py-4">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-4 flex-1" />
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-6 w-20 rounded-full" />
                  <Skeleton className="h-8 w-20 rounded" />
                  <Skeleton className="h-8 w-20 rounded" />
                </div>
              ))}
            </div>
          ) : !data?.items.length ? (
            <div className="py-16 text-center">
              <CheckCircle className="mx-auto mb-3 h-10 w-10 text-emerald-400" />
              <p className="font-medium">No pending approvals</p>
              <p className="mt-1 text-sm text-muted-foreground">All payments are up to date.</p>
            </div>
          ) : (
            <div className="divide-y">
              {data.items.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center gap-4 px-6 py-4 hover:bg-muted/30 transition-colors"
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-medium truncate">{item.payeeName}</p>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {item.paymentMode} · Submitted by {(item as any).creator?.name ?? 'Unknown'} · {formatDate(item.createdAt)}
                    </p>
                    {item.remarks && (
                      <p className="text-xs text-muted-foreground mt-0.5 italic truncate">{item.remarks}</p>
                    )}
                  </div>

                  <span className="text-sm font-semibold whitespace-nowrap">{formatAmount(item.amount)}</span>

                  <Badge variant="outline" className="text-amber-600 border-amber-300 bg-amber-50 whitespace-nowrap">
                    Pending
                  </Badge>

                  <div className="flex items-center gap-2 shrink-0">
                    <Button
                      size="sm"
                      className="bg-emerald-600 hover:bg-emerald-700 text-white"
                      onClick={() => setOtpPayment(item)}
                    >
                      <CheckCircle className="h-3.5 w-3.5 mr-1" />
                      Approve
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="text-red-600 border-red-300 hover:bg-red-50"
                      onClick={() => setRejectPayment(item)}
                    >
                      <XCircle className="h-3.5 w-3.5 mr-1" />
                      Reject
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <OTPModal
        payment={otpPayment}
        open={!!otpPayment}
        onClose={() => setOtpPayment(null)}
        onSuccess={handleActionSuccess}
      />
      <RejectDialog
        payment={rejectPayment}
        open={!!rejectPayment}
        onClose={() => setRejectPayment(null)}
        onSuccess={handleActionSuccess}
      />
    </>
  )
}

// ─── History tab ──────────────────────────────────────────────────────────────

function HistoryTab() {
  const { data, isLoading } = useQuery({
    queryKey: ['approvals-history'],
    queryFn: () => approvalsApi.history(),
    select: (res) => res.data,
  })

  return (
    <Card>
      <CardContent className="p-0">
        {isLoading ? (
          <div className="divide-y">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-4 px-6 py-4">
                <Skeleton className="h-4 w-32" />
                <Skeleton className="h-4 flex-1" />
                <Skeleton className="h-4 w-20" />
                <Skeleton className="h-6 w-20 rounded-full" />
                <Skeleton className="h-4 w-24" />
              </div>
            ))}
          </div>
        ) : !data?.items.length ? (
          <div className="py-16 text-center">
            <History className="mx-auto mb-3 h-10 w-10 text-slate-300" />
            <p className="font-medium">No approval history yet</p>
            <p className="mt-1 text-sm text-muted-foreground">Approved and rejected payments will appear here.</p>
          </div>
        ) : (
          <>
            {/* Table header */}
            <div className="grid grid-cols-[1fr_1fr_120px_100px_160px_1fr] gap-4 px-6 py-3 border-b bg-muted/40 text-xs font-medium text-muted-foreground uppercase tracking-wide">
              <span>Date</span>
              <span>Payee</span>
              <span>Amount</span>
              <span>Mode</span>
              <span>Action</span>
              <span>By / Reason</span>
            </div>

            <div className="divide-y">
              {data.items.map((item) => {
                const log = (item as any).approvalLogs?.[0]
                const actor = log?.actor?.name ?? '—'
                const reason = log?.reason ?? '—'
                const action = log?.action ?? item.status

                return (
                  <div
                    key={item.id}
                    className="grid grid-cols-[1fr_1fr_120px_100px_160px_1fr] gap-4 px-6 py-3.5 items-center text-sm hover:bg-muted/20 transition-colors"
                  >
                    <span className="text-muted-foreground text-xs">{formatDate(item.updatedAt ?? item.createdAt)}</span>

                    <div className="min-w-0">
                      <p className="font-medium truncate">{item.payeeName}</p>
                      {item.remarks && (
                        <p className="text-xs text-muted-foreground truncate italic">{item.remarks}</p>
                      )}
                    </div>

                    <span className="font-semibold">{formatAmount(item.amount)}</span>

                    <span className="text-muted-foreground">{item.paymentMode}</span>

                    {statusBadge(item.status)}

                    <div className="min-w-0">
                      <p className="truncate text-xs font-medium">{actor}</p>
                      {action === 'REJECTED' && reason !== '—' && (
                        <p className="truncate text-xs text-muted-foreground italic">{reason}</p>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function ApprovalsPage() {
  const [tab, setTab] = useState<string>('pending')

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <CheckCircle className="h-5 w-5 text-amber-500" />
        <div>
          <h1 className="text-lg font-semibold">Approvals</h1>
          <p className="text-sm text-muted-foreground">Review and action pending payments</p>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="pending">Pending</TabsTrigger>
          <TabsTrigger value="history">History</TabsTrigger>
        </TabsList>

        <TabsContent value="pending" className="mt-4">
          <PendingTab />
        </TabsContent>

        <TabsContent value="history" className="mt-4">
          <HistoryTab />
        </TabsContent>
      </Tabs>
    </div>
  )
}
